/* eslint-disable import/order */
import './utils/loadCss.js'
import './scss/global.scss'

// utils, helpers, actions, constants
import * as actions from './actions'
import * as constants from './constants'
import * as helpers from './helpers'
import * as reducers from './reducers'
import * as structures from './structures'
import * as utils from './utils'
import * as hooks from './hooks'
import favicon from './images/favicons/favicon.png'

// Import ui-core namespaces using star imports for proper type preservation
import * as uiCore from '@adc/ui-core'

// SEMANTIC (out-of-the-box)
import Semantic from './semanticUiExports'

// OVERRIDDEN
import Breadcrumb from './components/breadcrumb'
import Form from './components/form'
import Grid from './components/grid'

// HOMEGROWN
import {
  DATA_TYPES,
  SEARCH_TYPES,
  LOOKUP_TYPES,
} from './components/dataTable/searchField'
import ConfirmationModal from './components/confirmationModal'
import UnsavedChangesModal from './components/unsavedChangesModal'
import PresenceModal from './components/presenceModal'
import DataTable, {
  ActionIcon,
  COLUMN_TYPES,
  COLUMN_SORT_SETTING,
  COLUMN_STANDARD_SIZES,
  BulkProcessingModule,
  BulkUpdateDrawer,
  defineTableSchema,
} from './components/dataTable'
import DataTableUtils from './components/dataTable/utils'
import DataTableWrapper from './components/dataTableWrapper'
import ErrorAsyncSummary from './components/errorAsyncSummary'
import ErrorSyncSummary from './components/errorSyncSummary'
import FormWrapper, { FormWrapperUtils } from './components/formWrapper'
import ApolloFormWrapper from './components/formWrapper/apolloFormWrapper'
import LogoImage from './components/logoImage'
import MasterLayout from './components/masterLayout'
import NoMatch from './components/noMatch'
import InternalServerError from './components/internalServerError/index.jsx'
import PageHeader from './components/pageHeader'
import PrivateRoute from './components/privateRoute'
import PageLayout from './components/pageLayout'
import ResizablePanelGroup from './components/resizablePanel'
import StatusIcon from './components/statusIcon'
import AuditHistoryDrawer from './components/auditHistoryDrawer'
import Tab from './components/tab'
import MorpheusApplicationShell from './components/applicationShell'
import * as MorpheusApplicationContext from './components/applicationShell/context'
import * as BidderComponents from './components/bidderComponents'
import MorpheusReduxFormFields from './components/formFields'
import PhoneVerification from './components/phoneVerification/index.jsx'
import {
  ClosingOverview,
  ClosingGroup,
  ClosingGroupContainer,
  TransactionCoordinatorsContainer,
  TransactionCoordinator,
  AssociatedParties,
} from './components/closing'
import Filter from './components/filter'
import FilterToolbar from './components/filterToolbar'

export {
  Semantic,
  // OVERRIDDEN
  Breadcrumb,
  Form,
  Grid,
  Tab,
  // HOMEGROWN
  AuditHistoryDrawer,
  ConfirmationModal,
  UnsavedChangesModal,
  MorpheusReduxFormFields,
  PresenceModal,
  ErrorAsyncSummary,
  ErrorSyncSummary,
  Filter,
  FilterToolbar,
  FormWrapper,
  FormWrapperUtils,
  ApolloFormWrapper,
  LogoImage,
  MasterLayout,
  NoMatch,
  InternalServerError,
  PageHeader,
  PrivateRoute,
  PageLayout,
  ResizablePanelGroup,
  StatusIcon,
  MorpheusApplicationShell,
  MorpheusApplicationContext,
  BidderComponents,
  // DATA TABLE
  ActionIcon,
  COLUMN_TYPES,
  COLUMN_STANDARD_SIZES,
  COLUMN_SORT_SETTING,
  DataTable,
  DataTableUtils,
  DataTableWrapper,
  BulkProcessingModule,
  BulkUpdateDrawer,
  defineTableSchema,
  // SEARCH
  DATA_TYPES,
  SEARCH_TYPES,
  LOOKUP_TYPES,
  PhoneVerification,
  ClosingOverview,
  ClosingGroup,
  ClosingGroupContainer,
  TransactionCoordinatorsContainer,
  TransactionCoordinator,
  AssociatedParties,
}

// Namespaces named exports
export {
  actions,
  constants,
  helpers,
  reducers,
  structures,
  utils,
  favicon,
  hooks,
}

// translations
export * from '../src/translations'

export {
  AiChatContainer,
  Alert,
  AlertRole,
  Animate,
  Accordion,
  ApplicationNotificationsConsumer,
  ApplicationNotificationsProvider,
  Button,
  Carousel,
  Card,
  CardContainer,
  CheckboxField,
  CodeListItem,
  CognitionMessage,
  CountdownButton,
  CurrencyInputField,
  Drawer,
  EnhancedDrawer,
  ExpandableCard,
  Dropdown,
  DropdownField,
  FixedMessage,
  FieldTooltip,
  getNullApplicationNotifications,
  Header,
  Icon,
  InitialsAvatar,
  InputField,
  InlineInput,
  InlineSelectField,
  EditableListInputField,
  InfoPill,
  JsonBeforeAfter,
  JsonEditor,
  JsonViewer,
  Label,
  LabelValue,
  LabeledProgressBar,
  ListingBadges,
  ListingSearchField,
  TransactionSearchField,
  Loader,
  MarkdownRenderer,
  MaskedInputField,
  Menu,
  Message,
  MobileNav,
  MockApplicationNotificationsProvider,
  Modal,
  Money,
  Pagination,
  PhotoCard,
  PhotoCarouselModal,
  PhotoManagement,
  PhotoManagementModal,
  Popup,
  PopupTooltip,
  Pulser,
  RadioGroupField,
  RadioItem,
  SearchField,
  SearchFilter,
  SelectField,
  SortableList,
  StandardFieldLabel,
  Table,
  Text,
  TextAreaField,
  TextTruncate,
  Transition,
  useApplicationNotifications,
  useApplicationModals,
  ApplicationModalsProvider,
  ApplicationModalContext,
  ApplicationThemeProvider,
  useApplicationTheme,
  withApplicationTheme,
  ModalIds,
  VerticalExpander,
  withApplicationNotificationsContext,
  DEFAULT_NOTIFICATION_OPTIONS,
  FIELD_PLACEHOLDER_LENGTHS,
  NOTIFICATION_ROLES,
  OnlineAuctionSegmentBidRankList,
  FileDropzone,
  DropzoneField,
  UpdateOfferModalButton,
  CounterOfferModalButton,
  ShallowTaskWorkflow,
  CopyButton,
  Chip,
  ChipColor,
  ButtonColors,
  Divider,
  Statistic,
  getMappingPillColors,
} from '@adc/ui-core'

// Export all types from ui-core (AutomationTarget, SlottableComponent, etc.)
export type * from '@adc/ui-core'

// Export ui-core namespaces with proper type preservation
export const coreConstants = uiCore.constants
export const coreHooks = uiCore.hooks
export const coreUtils = uiCore.utils
